import { Hash, LockKeyhole, MailCheck, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { applyNumberSeriesChange, cancelNumberSeriesChange, extractErrorMessage, fetchNumberSeries, requestNumberSeriesChange, resendNumberSeriesCode, verifyNumberSeriesCode } from "../../lib/api";

const inputClass = "w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900";
const primaryClass = "rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:opacity-60";
const ghostClass = "rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900 disabled:opacity-60";

const useCountdown = (untilIso) => {
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => {
    if (!untilIso) {
      setSecondsLeft(0);
      return undefined;
    }
    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((new Date(untilIso).getTime() - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [untilIso]);
  return secondsLeft;
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-zinc-900" data-testid={testId}>{value}</p>
  </div>
);

const StepBadge = ({ step, active, done, label }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${done ? "bg-emerald-100 text-emerald-800" : active ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500"}`}>
    {step}. {label}
  </span>
);

const RequestStep = ({ snapshot, busy, onSubmit }) => {
  const [value, setValue] = useState("");
  const number = Number.parseInt(value, 10);
  const valid = Number.isInteger(number) && number > snapshot.highest_load_number;
  return (
    <form className="space-y-3" data-testid="number-series-request-form" onSubmit={(event) => { event.preventDefault(); if (valid) onSubmit(number); }}>
      <label className="block space-y-2">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Start the next load at</span>
        <input className={inputClass} data-testid="number-series-next-input" inputMode="numeric" min={snapshot.highest_load_number + 1} onChange={(event) => setValue(event.target.value.replace(/\D/g, ""))} placeholder={`Higher than ${snapshot.highest_load_number}`} type="number" value={value} />
      </label>
      {value && !valid ? <p className="text-xs text-red-600" data-testid="number-series-next-error">Must be higher than the highest existing load number ({snapshot.highest_load_number}).</p> : null}
      {valid ? <p className="text-xs text-zinc-600" data-testid="number-series-preview">Next loads and their invoices would be numbered <strong>{number}</strong>, {number + 1}, {number + 2}… Existing loads and invoices stay as they are.</p> : null}
      <div className="flex flex-wrap items-center gap-3">
        <button className={primaryClass} data-testid="number-series-request-button" disabled={!valid || busy} type="submit"><span className="inline-flex items-center gap-2"><MailCheck className="h-4 w-4" />{busy ? "Sending code…" : `Email confirmation code to ${snapshot.owner_email}`}</span></button>
      </div>
    </form>
  );
};

const CodeStep = ({ request, busy, onVerify, onResend, onCancel }) => {
  const [code, setCode] = useState("");
  const resendIn = useCountdown(request.can_resend_at);
  const expiresIn = useCountdown(request.code_expires_at);
  return (
    <form className="space-y-3" data-testid="number-series-code-form" onSubmit={(event) => { event.preventDefault(); if (code.length === 6) onVerify(code); }}>
      <p className="text-sm text-zinc-700" data-testid="number-series-code-note">A 6-digit code was emailed to <strong>{request.sent_to}</strong> for changing the next load number from <strong>{request.previous_next_number}</strong> to <strong>{request.new_next_number}</strong>. {expiresIn ? `It expires in ${Math.floor(expiresIn / 60)}:${String(expiresIn % 60).padStart(2, "0")}.` : "It has expired — request a new code."}</p>
      <label className="block space-y-2">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Confirmation code</span>
        <input autoComplete="one-time-code" className={`${inputClass} max-w-xs tracking-[0.4em]`} data-testid="number-series-code-input" inputMode="numeric" maxLength={6} onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" value={code} />
      </label>
      <p className="text-xs text-zinc-500" data-testid="number-series-attempts-left">{request.attempts_left} attempt(s) left.</p>
      <div className="flex flex-wrap items-center gap-3">
        <button className={primaryClass} data-testid="number-series-verify-button" disabled={code.length !== 6 || busy} type="submit">{busy ? "Checking…" : "Verify code"}</button>
        <button className={ghostClass} data-testid="number-series-resend-button" disabled={busy || resendIn > 0} onClick={onResend} type="button">{resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}</button>
        <button className={ghostClass} data-testid="number-series-cancel-button" disabled={busy} onClick={onCancel} type="button">Cancel</button>
      </div>
    </form>
  );
};

const ConfirmStep = ({ request, busy, onApply, onCancel }) => {
  const [word, setWord] = useState("");
  return (
    <form className="space-y-3" data-testid="number-series-confirm-form" onSubmit={(event) => { event.preventDefault(); if (word === "CONFIRM") onApply(word); }}>
      <p className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800" data-testid="number-series-verified-badge"><ShieldCheck className="h-4 w-4" /> Code verified</p>
      <p className="text-sm text-zinc-700" data-testid="number-series-confirm-note">Final check: the next load number will become <strong>{request.new_next_number}</strong> (currently {request.previous_next_number}). Loads and invoices created from now on continue {request.new_next_number}, {request.new_next_number + 1}, {request.new_next_number + 2}… Nothing already created is renumbered.</p>
      <label className="block space-y-2">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Type CONFIRM to apply</span>
        <input className={`${inputClass} max-w-xs`} data-testid="number-series-confirm-input" onChange={(event) => setWord(event.target.value.toUpperCase())} placeholder="CONFIRM" value={word} />
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <button className={primaryClass} data-testid="number-series-apply-button" disabled={word !== "CONFIRM" || busy} type="submit"><span className="inline-flex items-center gap-2"><LockKeyhole className="h-4 w-4" />{busy ? "Applying…" : "Apply new series"}</span></button>
        <button className={ghostClass} data-testid="number-series-cancel-button" disabled={busy} onClick={onCancel} type="button">Cancel</button>
      </div>
    </form>
  );
};

export const NumberSeriesSection = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setSnapshot(await fetchNumberSeries());
    } catch (error) {
      if (error?.response?.status === 403) {
        setForbidden(true);
        return;
      }
      toast.error(extractErrorMessage(error, "Unable to load the load number series."));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const run = async (action, successMessage) => {
    try {
      setBusy(true);
      const result = await action();
      toast.success(result?.message || successMessage);
      await refresh();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to update the load number series."));
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (forbidden || !snapshot) {
    return null;
  }

  const request = snapshot.pending_request;
  const stage = request?.status === "verified" ? "confirm" : request?.status === "pending" ? "code" : "request";

  return (
    <section className="panel-surface px-6 py-6" data-testid="settings-number-series-section">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Numbering</p>
          <h2 className="mt-2 flex items-center gap-2 text-3xl font-semibold text-zinc-900"><Hash className="h-6 w-6 text-zinc-400" />Load number series</h2>
          <p className="mt-2 max-w-2xl text-sm text-zinc-600">Load numbers run in sequence and each invoice takes its load's number. Changing the series only affects loads created after the change; every request and approval is recorded in the Audit Trail.</p>
        </div>
        <div className="grid min-w-[260px] grid-cols-2 gap-3">
          <Stat label="Highest load #" testId="number-series-highest" value={snapshot.highest_load_number} />
          <Stat label="Next load #" testId="number-series-next" value={snapshot.next_load_number} />
        </div>
      </div>

      {snapshot.can_manage ? (
        <div className="mt-6 space-y-4 rounded-[24px] border border-zinc-200 bg-white px-5 py-5">
          <div className="flex flex-wrap gap-2" data-testid="number-series-steps">
            <StepBadge active={stage === "request"} done={stage !== "request"} label="New start" step={1} />
            <StepBadge active={stage === "code"} done={stage === "confirm"} label="Email code" step={2} />
            <StepBadge active={stage === "confirm"} done={false} label="Type CONFIRM" step={3} />
          </div>
          {stage === "request" ? <RequestStep busy={busy} onSubmit={(number) => run(() => requestNumberSeriesChange(number), "Confirmation code sent.")} snapshot={snapshot} /> : null}
          {stage === "code" ? <CodeStep busy={busy} onCancel={() => run(() => cancelNumberSeriesChange(request.id), "Request cancelled.")} onResend={() => run(() => resendNumberSeriesCode(request.id), "Code re-sent.")} onVerify={(code) => run(() => verifyNumberSeriesCode(request.id, code), "Code accepted.")} request={request} /> : null}
          {stage === "confirm" ? <ConfirmStep busy={busy} onApply={(word) => run(() => applyNumberSeriesChange(request.id, word), "Series updated.")} onCancel={() => run(() => cancelNumberSeriesChange(request.id), "Request cancelled.")} request={request} /> : null}
        </div>
      ) : (
        <p className="mt-6 rounded-2xl border border-dashed border-zinc-300 px-4 py-3 text-sm text-zinc-600" data-testid="number-series-readonly-note">Only <strong>{snapshot.owner_email}</strong> can change the series — confirmation codes are sent to that inbox only.</p>
      )}
    </section>
  );
};
